import { useState, useEffect } from "react";
import api from "../../axios";
import { X, CheckCircle2, XCircle, AlertTriangle, ShieldAlert } from "lucide-react";

const ApprovalModal = ({ 
  isOpen, 
  onClose, 
  projectId, 
  actionType, 
  refreshProject,
  statusType = null, // 'approved', 'rejected', 'banned', or null for action confirmation
  projectTitle = "" 
}) => {
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAnimation, setShowAnimation] = useState(false);


  // 

  useEffect(() => {
    if (isOpen && statusType) {
      
      setShowAnimation(true);
      // Reset animation state after the animation completes
      const timer = setTimeout(() => {
        setShowAnimation(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, statusType]);

  if (!isOpen) return null;

  // If statusType is provided, we're showing a result modal
  // Otherwise, we're showing the action confirmation modal
  const isResultModal = ['approved', 'rejected', 'banned'].includes(statusType);


  const handleSubmit = async () => {
    if (!comment.trim()) {
      setError("Comment is required.");
      return;
    }

    setLoading(true);
    try {

        await api.post(`/projects/${projectId}/update-status`, {
          status: actionType, // 'approved', 'rejected', or 'banned'
          comment: comment,
        });

        refreshProject(); // Refresh the project after status change
      
        onClose(); // Close modal


    } catch (error) {
      console.error("Error updating status:", error);
      setError("Failed to update project status.");
    } finally {
      setLoading(false);
    }
  };



    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-w-full mx-4 transform transition-all duration-300 ease-in-out">
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {actionType === "approved" ? "Approve Project" : 
               actionType === "rejected" ? "Reject Project" : "Ban Project"}
            </h2>
            <button 
              onClick={onClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mt-4">
            {actionType === "approved"
              ? "Are you sure you want to approve this project? Add a note before confirming."
              : actionType === "rejected"
              ? "Are you sure you want to reject this project? Please provide a reason."
              : "Are you sure you want to ban this project? This action will completely remove access and cannot be easily reversed."}
          </p>

          <textarea
            className="w-full mt-4 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            rows="3"
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          ></textarea>

          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

          <div className="flex justify-end mt-4 space-x-3">
            <button
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className={`px-4 py-2 text-white rounded-md transition-all ${
                actionType === "approved" ? "bg-green-600 hover:bg-green-700" : 
                actionType === "rejected" ? "bg-red-600 hover:bg-red-700" : 
                "bg-orange-600 hover:bg-orange-700"
              } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Processing..." : actionType === "approved" ? "Confirm Approve" : actionType === "rejected" ? "Confirm Reject" : "Confirm Ban"}
            </button>
          </div>
        </div>
      </div>
    );
  

  // Result Modal Components
  const statusConfig = {
    approved: {
      icon: <CheckCircle2 className={`h-16 w-16 text-green-500 ${showAnimation ? 'scale-125' : 'scale-100'} transition-all duration-700`} />,
      title: "Project Approved",
      description: "The project has been successfully approved and is now publicly visible.",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      buttonColor: "bg-green-600 hover:bg-green-700"
    },
    rejected: {
      icon: <XCircle className={`h-16 w-16 text-red-500 ${showAnimation ? 'scale-125' : 'scale-100'} transition-all duration-700`} />,
      title: "Project Rejected",
      description: "The project has been rejected. The user will be notified with your feedback.",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      buttonColor: "bg-red-600 hover:bg-red-700"
    },
    banned: {
      icon: <ShieldAlert className={`h-16 w-16 text-orange-500 ${showAnimation ? 'scale-125 rotate-12' : 'scale-100 rotate-0'} transition-all duration-700`} />,
      title: "Project Banned",
      description: "The project has been banned and is no longer accessible. This action has been logged.",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      buttonColor: "bg-orange-600 hover:bg-orange-700"
    }
  };

  const config = statusConfig[statusType];

  // Result Modal (New functionality)
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${config.bgColor} border ${config.borderColor} rounded-lg shadow-xl p-6 w-96 max-w-full mx-4 transform transition-all duration-300 ease-in-out`}>
        <div className="flex justify-end">
          <button 
            onClick={onClose}
            className="rounded-full p-1 hover:bg-white hover:bg-opacity-50 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        
        <div className="flex flex-col items-center text-center pt-2 pb-6">
          <div className={`mb-4 ${showAnimation ? 'animate-pulse' : ''}`}>
            {config.icon}
          </div>
          
          <h2 className="text-xl font-bold mb-2">{config.title}</h2>
          
          {projectTitle && (
            <div className="mb-2">
              <span className="font-medium text-gray-700">"{projectTitle}"</span>
            </div>
          )}
          
          <p className="text-gray-600">{config.description}</p>
          
          <div className="mt-6">
            <button
              className={`px-6 py-2 text-white rounded-md transition-all ${config.buttonColor}`}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal;